/**
 * Game Statistics Service
 * - Local storage for detailed private history
 * - Supabase sync for public aggregate stats
 */
import { supabase } from '@/integrations/supabase/client';
import { GameType, GAME_INFO } from './gameService';
import { safeSetItem } from '@/lib/safeStorage';

// ── Types ──

export interface GameRecord {
  id: string;
  gameType: GameType;
  opponentUid: string;
  result: 'win' | 'loss' | 'draw';
  score: number;
  label: string;
  playedAt: number;
}

export interface GameStatsAggregate {
  gameType: GameType;
  totalGames: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
  bestScore: number;
  bestScoreLabel: string;
  winRate: number;
}

export interface ContactGameSummary {
  totalGames: number;
  totalWins: number;
  totalLosses: number;
  favoriteGame: GameType | null;
  recentGames: GameRecord[];
}

export interface PublicGameStats {
  gameType: string;
  totalGames: number;
  totalWins: number;
  bestScore: number;
  bestScoreLabel: string | null;
}

// ── Anti-cheat: HMAC Integrity ──

const INTEGRITY_KEY = 'aphenis_game_integrity';
const BAN_KEY = 'aphenis_anticheat_ban';
const BAN_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Check if user is currently banned from online games */
export function isCheatBanned(): boolean {
  try {
    const raw = localStorage.getItem(BAN_KEY);
    if (!raw) return false;
    const { until } = JSON.parse(raw);
    if (Date.now() < until) return true;
    // Ban expired — clean up
    localStorage.removeItem(BAN_KEY);
    return false;
  } catch { return false; }
}

/** Get ban expiry timestamp (0 if not banned) */
export function getBanExpiry(): number {
  try {
    const raw = localStorage.getItem(BAN_KEY);
    if (!raw) return 0;
    const { until } = JSON.parse(raw);
    return Date.now() < until ? until : 0;
  } catch { return 0; }
}

/** Issue a 24hr ban for cheating */
export function issueBan(): void {
  const until = Date.now() + BAN_DURATION_MS;
  try { safeSetItem(BAN_KEY, JSON.stringify({ until, issuedAt: Date.now() })); } catch {}
  console.warn('[AntiCheat] 24hr ban issued for data tampering');
}

function getIntegritySecret(): string {
  // Derive from user's signing public key — unique per identity
  try {
    const raw = localStorage.getItem('vaultmsg_uid');
    if (raw) {
      const identity = JSON.parse(raw);
      if (identity.signingPublicKey) return identity.signingPublicKey.slice(0, 32);
    }
  } catch {}
  return 'aphenis_default_integrity_seed_v1';
}

async function computeHMAC(data: string): Promise<string> {
  const secret = getIntegritySecret();
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(data));
  return Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function signAndStore(key: string, data: any): Promise<void> {
  const json = JSON.stringify(data);
  const hmac = await computeHMAC(json);
  try { safeSetItem(key, json); safeSetItem(key + '_sig', hmac); } catch {}
}

async function loadAndVerify<T>(key: string, fallback: T): Promise<{ data: T; valid: boolean }> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { data: fallback, valid: true };
    const storedSig = localStorage.getItem(key + '_sig');
    const data = JSON.parse(raw) as T;
    if (!storedSig) {
      // Legacy data without signature — sign it now (migration)
      await signAndStore(key, data);
      return { data, valid: true };
    }
    const expectedSig = await computeHMAC(raw);
    if (expectedSig !== storedSig) {
      console.warn(`[AntiCheat] Integrity check failed for ${key} — data may have been tampered with`);
      // Issue ban on tamper detection
      issueBan();
      return { data: fallback, valid: false };
    }
    return { data, valid: true };
  } catch { return { data: fallback, valid: true }; }
}

// ── Local Storage Keys ──

const HISTORY_KEY = 'aphenis_game_history';
const PUBLIC_TOGGLE_KEY = 'aphenis_game_stats_public';

// ── Local History ──

function getHistory(): GameRecord[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch { return []; }
}

function saveHistory(records: GameRecord[]): void {
  try { safeSetItem(HISTORY_KEY, JSON.stringify(records)); } catch {}
  // Async sign — fire and forget
  signAndStore(HISTORY_KEY, records).catch(() => {});
}

export function recordGame(record: Omit<GameRecord, 'id' | 'playedAt'>, gameMode?: string): GameRecord {
  const full: GameRecord = {
    ...record,
    id: crypto.randomUUID().slice(0, 12),
    playedAt: Date.now(),
  };
  const history = getHistory();
  history.unshift(full); // newest first
  // Keep max 500 records
  if (history.length > 100) history.length = 100;
  saveHistory(history);
  // Only sync to leaderboard for online PvP games — blocked when banned
  if (gameMode === 'online' && !isCheatBanned()) {
    syncStatsToDb(record.gameType).catch(() => {});
  }
  return full;
}

export function getGameHistory(filters?: {
  gameType?: GameType;
  opponentUid?: string;
  limit?: number;
}): GameRecord[] {
  let records = getHistory();
  if (filters?.gameType) records = records.filter(r => r.gameType === filters.gameType);
  if (filters?.opponentUid) records = records.filter(r => r.opponentUid === filters.opponentUid);
  if (filters?.limit) records = records.slice(0, filters.limit);
  return records;
}

export function getOverallStats(): GameStatsAggregate[] {
  const history = getHistory();
  const byType = new Map<GameType, GameRecord[]>();
  for (const r of history) {
    if (!byType.has(r.gameType)) byType.set(r.gameType, []);
    byType.get(r.gameType)!.push(r);
  }
  
  const stats: GameStatsAggregate[] = [];
  for (const [gameType, records] of byType) {
    const wins = records.filter(r => r.result === 'win').length;
    const losses = records.filter(r => r.result === 'loss').length;
    const draws = records.filter(r => r.result === 'draw').length;
    const bestRecord = records.reduce((best, r) => r.score > best.score ? r : best, records[0]);
    stats.push({
      gameType,
      totalGames: records.length,
      totalWins: wins,
      totalLosses: losses,
      totalDraws: draws,
      bestScore: bestRecord.score,
      bestScoreLabel: bestRecord.label,
      winRate: records.length > 0 ? Math.round((wins / records.length) * 100) : 0,
    });
  }
  return stats.sort((a, b) => b.totalGames - a.totalGames);
}

export function getContactStats(opponentUid: string): ContactGameSummary {
  const records = getHistory().filter(r => r.opponentUid === opponentUid);
  const wins = records.filter(r => r.result === 'win').length;
  const losses = records.filter(r => r.result === 'loss').length;

  // Favorite game
  const gameCounts = new Map<GameType, number>();
  for (const r of records) {
    gameCounts.set(r.gameType, (gameCounts.get(r.gameType) || 0) + 1);
  }
  let favoriteGame: GameType | null = null;
  let maxCount = 0;
  for (const [type, count] of gameCounts) {
    if (count > maxCount) { favoriteGame = type; maxCount = count; }
  }

  return {
    totalGames: records.length,
    totalWins: wins,
    totalLosses: losses,
    favoriteGame,
    recentGames: records.slice(0, 10),
  };
}

// ── Public Toggle ──

export function isStatsPublic(): boolean {
  return localStorage.getItem(PUBLIC_TOGGLE_KEY) === 'true';
}

export function setStatsPublic(value: boolean): void {
  try { safeSetItem(PUBLIC_TOGGLE_KEY, value ? 'true' : 'false'); } catch {}
  if (value) {
    // Sync all stats to DB
    syncAllStatsToDb().catch(() => {});
  } else {
    // Set all stats to private in DB
    hideAllStatsInDb().catch(() => {});
  }
}

// ── DB Sync ──

async function getMyUid(): Promise<string | null> {
  try {
    const raw = localStorage.getItem('vaultmsg_uid');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.uid || null;
    }
  } catch {}
  return null;
}

async function syncStatsToDb(gameType: GameType): Promise<void> {
  const uid = await getMyUid();
  if (!uid) return;
  
  const records = getHistory().filter(r => r.gameType === gameType);
  if (records.length === 0) return;
  
  const wins = records.filter(r => r.result === 'win').length;
  const bestRecord = records.reduce((best, r) => r.score > best.score ? r : best, records[0]);

  const { secureGameDataWrite } = await import('./secureApiClient');
  await secureGameDataWrite('game_stats', 'upsert', uid, {
    user_uid: uid,
    game_type: gameType,
    total_games: records.length,
    total_wins: wins,
    best_score: bestRecord.score,
    best_score_label: bestRecord.label,
    is_public: true,
    updated_at: new Date().toISOString(),
    _onConflict: 'user_uid,game_type',
  });
}

async function syncAllStatsToDb(): Promise<void> {
  const allStats = getOverallStats();
  for (const stat of allStats) {
    await syncStatsToDb(stat.gameType);
  }
}

async function hideAllStatsInDb(): Promise<void> {
  const uid = await getMyUid();
  if (!uid) return;
  
  // We can't do a bulk update easily, so update each
  const allStats = getOverallStats();
  const { secureGameDataWrite } = await import('./secureApiClient');
  for (const stat of allStats) {
    await secureGameDataWrite('game_stats', 'upsert', uid, {
      user_uid: uid,
      game_type: stat.gameType,
      total_games: stat.totalGames,
      total_wins: stat.totalWins,
      best_score: stat.bestScore,
      best_score_label: stat.bestScoreLabel,
      is_public: false,
      updated_at: new Date().toISOString(),
      _onConflict: 'user_uid,game_type',
    });
  }
}

/** Fetch another user's public game stats */
export async function fetchPublicStats(userUid: string): Promise<PublicGameStats[]> {
  const { data, error } = await supabase
    .from('game_stats')
    .select('game_type, total_games, total_wins, best_score, best_score_label')
    .eq('user_uid', userUid)
    .eq('is_public', true);

  if (error || !data) return [];
  return data.map(d => ({
    gameType: d.game_type,
    totalGames: d.total_games,
    totalWins: d.total_wins,
    bestScore: d.best_score,
    bestScoreLabel: d.best_score_label,
  }));
}

export function clearGameHistory(): void {
  localStorage.removeItem(HISTORY_KEY);
  localStorage.removeItem(HISTORY_KEY + '_sig');
}

/** Verify integrity of game history — returns false if tampered */
export async function verifyGameIntegrity(): Promise<boolean> {
  const { valid } = await loadAndVerify<GameRecord[]>(HISTORY_KEY, []);
  return valid;
}

/** Re-sign current data (call after identity is loaded) */
export async function resealGameData(): Promise<void> {
  const history = getHistory();
  await signAndStore(HISTORY_KEY, history);
}
